import { cancelable } from 'cancelable-promise';
import { ASSET_TYPE } from '../../constants/Constants';
import { OpenInOfficeOnlineHelper } from '../../helpers/OpenInOfficeOnlineHelper';
import { ThumbnailsHelper } from '../../helpers/ThumbnailsHelper';
import { PresentationHelper } from '../../helpers/PresentationHelper';
import { ErrorPresentationRenderer } from '../renderers/ErrorPresentationRenderer';
import { ObservableDimensionsIframePresentationRenderer } from '../renderers/ObservableDimensionsIframePresentationRenderer';
import { ThumbnailErrorPresentationRenderer } from '../renderers/ThumbnailErrorPresentationRenderer';
import { LayoutHorizontal } from '../layouts/LayoutHorizontal';
import { AssetViewerPresentationRenderer } from '../renderers/AssetViewerPresentationRenderer';
import { PdfJsPresentationRenderer } from '../renderers/PdfJsPresentationRenderer';
import { ThreeJsPresentationRenderer } from '../renderers/ThreeJsPresentationRenderer';
import { UrlPresentationRenderer } from '../renderers/UrlPresentationRenderer';
import { UrlIframePresentationRenderer } from '../renderers/UrlIframePresentationRenderer';
import { AssetViewer } from '../AssetViewer';
import { RenderState } from '../../types/RenderState.type';
import { ViewerSelectViewEvent, ViewerDeselectViewEvent, ViewerUpdateEvent, ViewerOpenInOfficeOnlineEvent, ViewerViewRenderedEvent, ViewerDownloadAssetEvent } from '../../types/ViewerEvents.type';
import { AssetViewerViewMapper } from '../../helpers/AssetViewerViewMapper';
import { PdfViewerPresentationRenderer } from '../renderers/PdfViewerPresentationRenderer';
const AnnotationHelper = require('@showpad/annotation-drawer-js/lib/AnnotationHelper').default;
const CLASS_LOADING = 'loading';
const CLASS_LOADING_LONG = 'loading-long';
export class AssetViewerView {
    constructor(config, eventBus, viewerLayout, assetViewerItem, tokenService, pdfJsLinkService, id, page, // page is 1-based
    annotations) {
        this.config = config;
        this.eventBus = eventBus;
        this.viewerLayout = viewerLayout;
        this.assetViewerItem = assetViewerItem;
        this.tokenService = tokenService;
        this.pdfJsLinkService = pdfJsLinkService;
        this.id = id;
        this.page = page;
        this.annotations = annotations;
        this._renderState = RenderState.Initial;
        this.previewRenderState = RenderState.Initial;
        this.annotationHelper = null;
        this.groupId = null;
        this.showLoaderTimeout = null;
        this.isExpanded = false;
        this.hasBeenDestroyed = false;
        this.viewState = {
            percent: 0,
            pixels: 0
        };
        this.isSelected = false;
        this.loadPresentationsPromise = null;
        this._contentElement = null;
        this._contentWrapper = null;
        this._container = null;
        this._checkbox = null;
        this._styledCheckbox = null;
        this._checkboxInput = null;
        this._presentInOfficeOnlineBar = null;
        this.initializePresentationRenderer();
        this.render = this.render.bind(this);
        this.renderPreview = this.renderPreview.bind(this);
        this.isWindowsDesktopApp = config.IS_WINDOWS_DESKTOP_APP;
    }
    set renderState(value) {
        this._renderState = value;
        this.eventBus.dispatch(new ViewerUpdateEvent());
    }
    get renderState() {
        return this._renderState;
    }
    get errorPresentation() {
        var _a;
        const pdfJsPresentationRenderer = (_a = this.presentationRenderers) === null || _a === void 0 ? void 0 : _a.find(presentationRenderer => !!presentationRenderer.lowQualityPreviewUrl);
        const previewUrl = pdfJsPresentationRenderer && pdfJsPresentationRenderer.lowQualityPreviewUrl;
        const dimensions = pdfJsPresentationRenderer && pdfJsPresentationRenderer.dimensions;
        return new ErrorPresentationRenderer(this, previewUrl, dimensions);
    }
    get url() {
        return this.presentationRenderer.url;
    }
    get isLoading() {
        var _a;
        return (_a = this.presentationRenderer) === null || _a === void 0 ? void 0 : _a.isLoading;
    }
    get shouldLoad() {
        return !this.assetViewerItem.presentations || !this.presentationRenderer.hasMetaData;
    }
    get isLoaded() {
        return !!this.assetViewerItem.presentations && !!this.presentationRenderer.hasMetaData;
    }
    get dimensions() {
        var _a;
        return (_a = this.presentationRenderer) === null || _a === void 0 ? void 0 : _a.dimensions;
    }
    get isActive() {
        return this.presentationRenderer.isActive;
    }
    get canBeDestroyed() {
        if (this.presentationRenderer.assetViewer instanceof AssetViewer) {
            return this.presentationRenderer.assetViewer.viewer.views.every(view => view.canBeDestroyed);
        }
        else {
            return !this.isActive;
        }
    }
    get defaultThumb() {
        return (this.config.DEFAULT_THUMB_BASE_PATH +
            ThumbnailsHelper.getDefaultThumb(this.assetViewerItem.type, this.assetViewerItem.extension));
    }
    initializePresentationRenderer() {
        if (!this.presentationRenderer) {
            if (this.assetViewerItem.presentations) {
                this.assetViewerItem.presentations = PresentationHelper.parsePresentations(this.assetViewerItem.presentations, this.assetViewerItem.page, this.assetViewerItem.downloadUrl);
            }
            this.presentationRenderers = PresentationHelper.createPresentationRenderers(this.config.PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP, this, this.pdfJsLinkService);
            this.presentationRenderer =
                this.presentationRenderers && this.presentationRenderers.length ? this.presentationRenderers[0] : null;
            // If we have presentations and we could not derive a renderer we fallback to an error renderer
            if (this.assetViewerItem.presentations && !this.presentationRenderer) {
                this.presentationRenderer = this.errorPresentation;
                // So the view does not get destroyed!
                this.presentationRenderer.isActive = true;
            }
        }
    }
    removeOnRenderEventListeners() { }
    loadPresentations() {
        if (this.assetViewerItem.presentations) {
            this.loadPresentationsPromise = cancelable(Promise.resolve());
        }
        if (this.loadPresentationsPromise) {
            return this.loadPresentationsPromise;
        }
        this.loadPresentationsPromise = cancelable(this.assetViewerItem.loadPresentations().then((result) => {
            this.assetViewerItem.presentations = result.presentations;
            this.assetViewerItem.embeddedData = result.embeddedData;
            this.assetViewerItem.assetFileId = result.assetFileId;
        }, () => {
            // When loading presentations fails we set the presentations to [] and fallback to the error renderer
            // If we do not we get in a loop!
            this.assetViewerItem.presentations = [];
            this.assetViewerItem.embeddedData = null;
            this.assetViewerItem.assetFileId = null;
        }));
        return this.loadPresentationsPromise;
    }
    load() {
        this.setLoaderTimeout();
        return this.loadPresentations()
            .then(() => this.initializePresentationRenderer())
            .then(() => this.presentationRenderer.load())
            .catch(error => {
            this.onPresentationError(error);
        });
    }
    get contentElement() {
        this._contentElement = this._contentElement || this.viewerLayout.createContentContainer(this);
        // TODO move this
        if (this.presentationRenderer &&
            !this.presentationRenderer.assetViewer &&
            !(this.presentationRenderer instanceof PdfViewerPresentationRenderer) &&
            this.assetViewerItem.type === ASSET_TYPE.DOCUMENT) {
            this._contentElement.style.background = 'white';
        }
        return this._contentElement;
    }
    set contentElement(element) {
        this._contentElement = element;
    }
    set contentWrapper(element) {
        this._contentWrapper = element;
    }
    get contentWrapper() {
        this._contentWrapper = this._contentWrapper || this.viewerLayout.createContentWrapper();
        return this._contentWrapper;
    }
    createPresentInOfficeOnlineBar() {
        const presentInOfficeOnlineBar = OpenInOfficeOnlineHelper.createPresentInOfficeOnlineBar(this.assetViewerItem.extension, this.config);
        const anchorElement = presentInOfficeOnlineBar.querySelector('a');
        if (anchorElement) {
            anchorElement.onclick = event => {
                event.preventDefault();
                this.eventBus.dispatch(new ViewerOpenInOfficeOnlineEvent({ assetViewerItem: this.assetViewerItem }));
            };
        }
        return presentInOfficeOnlineBar;
    }
    get presentInOfficeOnlineBar() {
        this._presentInOfficeOnlineBar = this._presentInOfficeOnlineBar || this.createPresentInOfficeOnlineBar();
        return this._presentInOfficeOnlineBar;
    }
    get checkbox() {
        this._checkbox = this._checkbox || this.viewerLayout.createCheckbox();
        return this._checkbox;
    }
    get styledCheckbox() {
        this._styledCheckbox = this._styledCheckbox || this.viewerLayout.createStyledCheckbox();
        return this._styledCheckbox;
    }
    get checkboxInput() {
        this._checkboxInput = this._checkboxInput || this.viewerLayout.createCheckboxInput();
        return this._checkboxInput;
    }
    get container() {
        this._container = this._container || this.viewerLayout.createContainer();
        return this._container;
    }
    set container(element) {
        this._container = element;
    }
    get canLazilyAppendView() {
        if (this.presentationRenderer instanceof AssetViewerPresentationRenderer ||
            this.presentationRenderer instanceof PdfJsPresentationRenderer ||
            this.presentationRenderer instanceof UrlPresentationRenderer ||
            this.presentationRenderer instanceof UrlIframePresentationRenderer) {
            return false;
        }
        if (!(this.viewerLayout instanceof LayoutHorizontal)) {
            return false;
        }
        return true;
    }
    renderPreview() {
        this.previewRenderState = RenderState.Running;
        this.hideLoader();
        if (typeof this.presentationRenderer.renderPreview === 'function') {
            return this.tokenService
                .ensureToken()
                .then(() => this.presentationRenderer.renderPreview(this.contentElement))
                .then(() => {
                this.previewRenderState = RenderState.Finished;
            }, error => {
                if (error && error.status === 401 && typeof this.config.refreshToken === 'function') {
                    return this.tokenService.refreshToken().then(() => {
                        return this.renderPreview();
                    });
                }
                throw error;
            });
        }
        this.previewRenderState = RenderState.Finished;
        return Promise.resolve();
    }
    render() {
        this.renderState = RenderState.Running;
        this.setLoaderTimeout();
        // TODO fix: find a generic way that is performant
        // TODO check if this is needed when rendering previews in Horizontal Layout
        if (!this.canLazilyAppendView) {
            this.contentWrapper.appendChild(this.contentElement);
            this.container.appendChild(this.contentWrapper);
            this.viewerLayout.renderViewContainer(this);
        }
        return this.tokenService
            .ensureToken()
            .then(() => this.presentationRenderer.render(this.contentElement, this.slideWidth, this.slideHeight))
            .then(() => this.onRender(), error => this.onPresentationError(error));
    }
    onRender() {
        this.renderState = RenderState.Finished;
        this.contentElement.classList.remove(CLASS_LOADING, CLASS_LOADING_LONG);
        this.contentElement.style.backgroundImage = 'none';
        // TODO fix: find a generic way that is performant
        if (this.canLazilyAppendView) {
            this.contentWrapper.appendChild(this.contentElement);
            this.container.appendChild(this.contentWrapper);
            this.viewerLayout.renderViewContainer(this);
        }
        this.hideLoader();
        if (this.shouldRenderAnnotations) {
            this.renderAnnotations();
        }
        this.eventBus.dispatch(new ViewerViewRenderedEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this) }));
    }
    onPreviewError(error) {
        console.error('PREVIEW ERROR', error);
        this.previewRenderState = RenderState.Failed;
    }
    onPresentationError(error) {
        if (error && error.status === 401 && typeof this.config.refreshToken === 'function') {
            this.tokenService.refreshToken();
            this.renderState = RenderState.Initial;
            if (typeof this.presentationRenderer.resetPdf === 'function') {
                this.presentationRenderer.resetPdf();
            }
            if (this.previewRenderState === RenderState.Failed) {
                if (typeof this.presentationRenderer.resetPreview === 'function') {
                    this.presentationRenderer.resetPreview();
                }
                this.previewRenderState = RenderState.Initial;
            }
            // render cycle will continue on error
            throw error;
        }
        this.setFallbackPresentation(error);
    }
    cancel() {
        if (this.loadPresentationsPromise) {
            this.loadPresentationsPromise.cancel();
            this.loadPresentationsPromise = null;
        }
        if (typeof this.presentationRenderer.cancel === 'function') {
            this.renderState = RenderState.Initial;
            this.presentationRenderer.cancel();
        }
    }
    destroy() {
        var _a;
        if (this.loadPresentationsPromise) {
            this.loadPresentationsPromise.cancel();
            this.loadPresentationsPromise = null;
        }
        this.removeOnRenderEventListeners();
        // TODO fix: find a generic way that is performant
        // if (this.viewerLayout instanceof LayoutHorizontal) {
        //     this.viewerLayout.destroyViewContainer(this);
        //     this.container = null;
        //     this.contentWrapper = null;
        //     this.contentElement = null;
        // }
        this.annotationsLayer = null;
        (_a = this.presentationRenderer) === null || _a === void 0 ? void 0 : _a.destroy();
        this.reset();
        this.hasBeenDestroyed = true;
    }
    // To be used on final destruction, break all the references to HTMLElements so they can be garbageCollected,
    // No need to remove the ViewContainer from the dom since the whole asset-viewer will be destroyed and they will be garbageCollected anyway
    destroyFinal() {
        var _a;
        this.removeOnRenderEventListeners();
        this.container = null;
        this.contentWrapper = null;
        this.contentElement = null;
        this.annotationsLayer = null;
        (_a = this.presentationRenderer) === null || _a === void 0 ? void 0 : _a.destroyFinal();
    }
    reset() {
        this.renderState = RenderState.Initial;
        if (this.presentationRenderer && typeof this.presentationRenderer.reset === 'function') {
            this.presentationRenderer.reset();
        }
    }
    resize() {
        if (this.presentationRenderer && this.presentationRenderer.assetViewer) {
            this.presentationRenderer.assetViewer.resize();
        }
        this.resizeAnnotations();
        if ((this.presentationRenderer && this.presentationRenderer instanceof ThreeJsPresentationRenderer) ||
            this.presentationRenderer instanceof PdfJsPresentationRenderer ||
            this.presentationRenderer instanceof PdfViewerPresentationRenderer) {
            this.presentationRenderer.resize();
        }
        if ((this.presentationRenderer && this.presentationRenderer instanceof UrlPresentationRenderer) ||
            this.presentationRenderer instanceof UrlIframePresentationRenderer ||
            this.presentationRenderer instanceof ObservableDimensionsIframePresentationRenderer) {
            this.presentationRenderer.resize();
            // calculate height based on the DOM element after it has been added to the DOM
            this.viewerLayout.update();
        }
    }
    get shouldRenderAnnotations() {
        const hasAnnotations = this.annotations && this.annotations.length;
        const isRendered = this.renderState === RenderState.Finished || this.previewRenderState === RenderState.Finished;
        const isErrorPresentation = this.presentationRenderer instanceof ErrorPresentationRenderer ||
            this.presentationRenderer instanceof ThumbnailErrorPresentationRenderer;
        const isDocument = this.assetViewerItem.type === ASSET_TYPE.DOCUMENT;
        const hasNestedViewer = this.presentationRenderer instanceof AssetViewerPresentationRenderer ||
            this.presentationRenderer instanceof PdfViewerPresentationRenderer;
        const annotationsAreAlreadyRendered = this.annotationsLayer && this.annotationsLayer.children;
        return !isErrorPresentation && isDocument && isRendered && hasAnnotations && !hasNestedViewer && !annotationsAreAlreadyRendered;
    }
    getAnnotationHelper() {
        if (!this.annotationHelper) {
            this.renderAnnotations();
        }
        return this.annotationHelper;
    }
    didAnnotationsChange() {
        if (!this.annotationHelper) {
            return false;
        }
        return this.annotationHelper.didAnnotationsChange();
    }
    getAnnotations() {
        let annotations = this.annotations || [];
        if (this.annotationHelper) {
            annotations = this.annotationHelper.getData();
        }
        if (annotations.length === 0) {
            annotations = null;
        }
        return annotations;
    }
    renderAnnotations() {
        const scale = this.slideWidth / this.dimensions.width;
        if (!this.annotationsLayer) {
            this.annotationsLayer = AssetViewerView.createAnnotationsLayer();
            this.contentElement.appendChild(this.annotationsLayer);
        }
        this.annotationsLayer.innerHTML = '';
        try {
            const annotations = this.annotations || [];
            this.annotationHelper = new AnnotationHelper(this.annotationsLayer, {
                meta: {},
                data: [...annotations]
            }, {
                create: this.config.CAN_CREATE_ANNOTATIONS,
                creatorZoom: scale,
                append: true
            });
            if (this.annotationHelper.creator) {
                this.annotationHelper.creator.setIsEnabled(false);
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    enableAnnotations() {
        if (this.annotationsLayer) {
            this.annotationsLayer.classList.add('annotations-layer--enabled');
        }
    }
    disableAnnotations() {
        if (this.annotationsLayer) {
            this.annotationsLayer.classList.remove('annotations-layer--enabled');
        }
    }
    resizeAnnotations() {
        if (this.annotationHelper) {
            this.annotationHelper.resize(this.currentScale);
        }
    }
    resetAnnotations() {
        if (this.presentationRenderer && this.presentationRenderer.assetViewer) {
            this.presentationRenderer.assetViewer.resetAnnotations();
        }
        if (this.presentationRenderer && this.presentationRenderer.pdfViewer) {
            this.presentationRenderer.pdfViewer.resetAnnotations();
        }
        if (this.annotationHelper) {
            this.annotationHelper.reset();
        }
    }
    setLoaderTimeout() {
        if (this.config.SHOW_VIEW_LOADER && !this.showLoaderTimeout) {
            this.showLoaderTimeout = setTimeout(() => this.showLoader(), this.config.SHOW_VIEW_LOADER_TIMEOUT);
        }
    }
    clearLoaderTimeout() {
        clearTimeout(this.showLoaderTimeout);
        this.showLoaderTimeout = null;
    }
    showLoader() {
        if (this.showLoaderTimeout) {
            requestAnimationFrame(() => {
                this.contentElement.classList.add(CLASS_LOADING_LONG);
                this.loader = AssetViewerView.renderLoader();
                this.contentElement.appendChild(this.loader);
            });
        }
    }
    hideLoader() {
        this.clearLoaderTimeout();
        requestAnimationFrame(() => {
            if (this.loader && this.contentElement.contains(this.loader)) {
                this.contentElement.removeChild(this.loader);
            }
        });
    }
    setFallbackPresentation(error) {
        console.warn('Setting fallback presentation', error, this);
        this.renderState = RenderState.Initial;
        const currentPresentationIndex = this.presentationRenderers && this.presentationRenderers.indexOf(this.presentationRenderer);
        const nextPresentation = this.presentationRenderers && this.presentationRenderers[currentPresentationIndex + 1];
        if (nextPresentation) {
            this.presentationRenderer = nextPresentation;
        }
        else {
            this.presentationRenderer = this.errorPresentation;
        }
    }
    download() {
        this.assetViewerItem.download();
        this.eventBus.dispatch(new ViewerDownloadAssetEvent({ assetViewerItem: this.assetViewerItem }));
    }
    selectView() {
        if (this.isSelected === false) {
            this.isSelected = true;
            this.eventBus.dispatch(new ViewerSelectViewEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this) }));
        }
    }
    deselectView() {
        if (this.isSelected === true) {
            this.isSelected = false;
            this.eventBus.dispatch(new ViewerDeselectViewEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this) }));
        }
    }
    static renderLoader() {
        const element = document.createElement('div');
        const innerElement = document.createElement('div');
        element.classList.add('av-loader');
        element.appendChild(innerElement);
        return element;
    }
    static createAnnotationsLayer() {
        const element = document.createElement('div');
        element.style.top = '0';
        element.style.left = '0';
        element.style.position = 'absolute';
        element.style.width = '100%';
        element.style.height = '100%';
        element.style.overflow = 'hidden';
        element.style.zIndex = '1';
        element.classList.add('annotations-layer');
        return element;
    }
}
// This is required as currently until we no longer use the 'buildManyAssetViewerViews'
export class ThumbnailAssetViewerView extends AssetViewerView {
    // TODO reuse constructor from parent
    constructor(config, eventBus, viewerLayout, assetViewerItem, tokenService, id, page, annotations) {
        super(config, eventBus, viewerLayout, assetViewerItem, tokenService, undefined, id, page, annotations);
        this.config = config;
        this.eventBus = eventBus;
        this.viewerLayout = viewerLayout;
        this.assetViewerItem = assetViewerItem;
        this.tokenService = tokenService;
        this.id = id;
        this.page = page;
        this.annotations = annotations;
        this.isCover = false;
        this.isExpanded = false;
        this.isFirstPageOfRange = false;
        this.isPartOfRange = false;
        if (this.presentationRenderer instanceof ThumbnailErrorPresentationRenderer) {
            this.presentationRenderer = this.errorPresentation;
        }
        this.addEventListeners();
    }
    get errorPresentation() {
        return new ThumbnailErrorPresentationRenderer(this);
    }
    get labelText() {
        return this.assetViewerItem.numViews > 1 && !this.isCover ? this.page.toString() : this.assetViewerItem.displayName;
    }
    onRender() {
        super.onRender();
        this.contentElement.classList.remove('loading');
        this.contentElement.style.backgroundImage = 'none';
    }
    addEventListeners() {
        // TODO bind event when container is created? instead of overwriting it every time?
        this.contentElement.onclick = () => this.onViewClick();
        // this.container.addEventListener("click", this.onViewClick.bind(this))
    }
    removeEventListeners() { }
    onViewClick() {
        this.linkService.view = this.linkedView;
        this.viewerLayout.setCurrentView(this.linkedView);
    }
}
//# sourceMappingURL=AssetViewerView.js.map